import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import api from '../services/api';
import './Photographers.css';

const Photographers = () => {
  usePageTitle('Photographers');
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);

  useEffect(() => {
    fetchPhotographers();
  }, []);

  const fetchPhotographers = async () => {
    try {
      const response = await api.get('/photographers');
      const photographersData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data && Array.isArray(response.data.data) 
          ? response.data.data 
          : []);
      
      setPhotographers(photographersData.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      console.error('Error fetching photographers:', error);
      setPhotographers([]);
    } finally {
      setLoading(false);
    }
  };

  // Placeholder images for photographers without photos
  const placeholderImages = [
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop&q=80',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=500&fit=crop&q=80',
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=500&fit=crop&q=80',
    'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=400&h=500&fit=crop&q=80',
  ];

  const getPhotoUrl = (photographer, index = 0) => {
    if (photographer.photo_url) {
      return photographer.photo_url;
    }
    if (photographer.photo_path) {
      if (photographer.photo_path.startsWith('http')) {
        return photographer.photo_path;
      }
      return `https://aliensteam.com/storage/${photographer.photo_path}`;
    }
    // Return a placeholder based on the photographer's id or index
    const placeholderIndex = (photographer.id || index) % placeholderImages.length;
    return placeholderImages[placeholderIndex];
  };

  const handlePhotoError = (e, photographer, index = 0) => {
    const placeholderIndex = (photographer.id || index) % placeholderImages.length;
    if (!e.target.src.includes('unsplash.com')) {
      e.target.src = placeholderImages[placeholderIndex];
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ar-EG', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const openPackagesModal = (photographer) => {
    setSelectedPhotographer(photographer);
    document.body.style.overflow = 'hidden';
  };

  const closePackagesModal = () => {
    setSelectedPhotographer(null);
    document.body.style.overflow = '';
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedPhotographer) {
        closePackagesModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedPhotographer]);

  if (loading) {
    return (
      <div className="photographers-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading photographers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="photographers-container">
      <div className="photographers-header">
        <h1>📸 PHOTOGRAPHERS</h1>
        <p className="photographers-subtitle">Meet our talented media team</p>
      </div>

      {photographers.length === 0 ? (
        <div className="no-photographers">
          <div className="no-photographers-icon">📷</div>
          <p>No photographers found at the moment.</p>
        </div>
      ) : (
        <div className="photographers-grid">
          {photographers.map((photographer) => (
            <div
              key={photographer.id}
              className="photographer-card"
              onClick={() => openPackagesModal(photographer)}
            >
              <div className="photographer-photo-container">
                <img
                  src={getPhotoUrl(photographer)}
                  alt={photographer.name}
                  className="photographer-photo"
                  onError={(e) => handlePhotoError(e, photographer)}
                />
              </div>
              <div className="photographer-info">
                <h3 className="photographer-name">{photographer.name}</h3>
                {photographer.name_arabic && (
                  <p className="photographer-name-arabic">{photographer.name_arabic}</p>
                )}
                <div className="view-packages-hint">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                  View Packages
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Packages Modal */}
      {selectedPhotographer && (
        <div
          className="packages-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePackagesModal();
          }}
        >
          <div className="packages-modal">
            <button className="close-modal-btn" onClick={closePackagesModal}>
              ×
            </button>
            
            <div className="modal-header">
              <img
                src={getPhotoUrl(selectedPhotographer)}
                alt={selectedPhotographer.name}
                className="modal-photographer-photo"
                onError={(e) => handlePhotoError(e, selectedPhotographer)}
              />
              {selectedPhotographer.name_arabic && (
                <p className="modal-photographer-name-arabic">{selectedPhotographer.name_arabic}</p>
              )}
              {selectedPhotographer.bio && (
                <p className="photographer-bio">{selectedPhotographer.bio}</p>
              )}
              
              {/* Contact Links */}
              {(selectedPhotographer.phone || selectedPhotographer.whatsapp || selectedPhotographer.instagram || selectedPhotographer.facebook) && (
                <div className="photographer-contacts">
                  {selectedPhotographer.phone && (
                    <a 
                      href={`tel:${selectedPhotographer.phone}`} 
                      className="contact-link" 
                      title="Call"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                      </svg>
                    </a>
                  )}
                  {selectedPhotographer.whatsapp && (
                    <a 
                      href={`https://wa.me/${selectedPhotographer.whatsapp.replace(/[^0-9]/g, '')}`} 
                      className="contact-link" 
                      title="WhatsApp" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </a>
                  )}
                  {selectedPhotographer.instagram && (
                    <a 
                      href={selectedPhotographer.instagram.startsWith('http') ? selectedPhotographer.instagram : `https://instagram.com/${selectedPhotographer.instagram}`} 
                      className="contact-link" 
                      title="Instagram" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </a>
                  )}
                  {selectedPhotographer.facebook && (
                    <a 
                      href={selectedPhotographer.facebook.startsWith('http') ? selectedPhotographer.facebook : `https://facebook.com/${selectedPhotographer.facebook}`} 
                      className="contact-link" 
                      title="Facebook" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </a>
                  )}
                </div>
              )}

              <h2 className="modal-photographer-name">{selectedPhotographer.name}</h2>
            </div>

            <div className="packages-section">
              <h3 className="packages-title">📦 Available Packages</h3>
              
              {selectedPhotographer.active_packages && selectedPhotographer.active_packages.length > 0 ? (
                <div className="packages-grid">
                  {selectedPhotographer.active_packages.map((pkg, index) => (
                    <div key={pkg.id} className={`package-card ${index === 1 ? 'featured' : ''}`}>
                      {index === 1 && <span className="package-badge">Popular</span>}
                      <h4 className="package-name">{pkg.name}</h4>
                      <div className="package-details">
                        {pkg.photos_count > 0 && (
                          <div className="package-item">
                            <span className="package-item-icon">📷</span>
                            <span><span className="package-item-value">{pkg.photos_count}</span> Photos</span>
                          </div>
                        )}
                        {pkg.videos_count > 0 && (
                          <div className="package-item">
                            <span className="package-item-icon">🎬</span>
                            <span><span className="package-item-value">{pkg.videos_count}</span> Videos</span>
                          </div>
                        )}
                      </div>
                      {pkg.description && (
                        <p className="package-description">{pkg.description}</p>
                      )}
                      <div className="package-price">{formatPrice(pkg.price)} EGP</div>
                      <span className="package-price-label">Starting Price</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-packages">
                  <p>No packages available at the moment. Contact the photographer directly for pricing.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Photographers;
